import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Badge, Box, Button, ButtonDropdown, Container, FormField, Header, Link, Modal, Select, SpaceBetween,
    Spinner, StatusIndicator, Textarea,
} from '@amzn/awsui-components-react';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { StatusIndicatorProps } from '@amzn/awsui-components-react/polaris/status-indicator';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import { TPEReactTable } from 'src/components/shared/TPEReactTable';
import ServiceCollection from 'src/services/ServiceCollection';
import { CommonBalancePoolColumn, CommonBalancePoolExecutionState, CommonBalancePoolExecutionStatus, CommonBalancePoolValidationResult } from 'src/models/multidimensional-allocation/CommonBalancePool';
import { TableType } from 'src/models/segment-cdt/SegmentCdtTypes';
import { WorksheetStatus } from 'src/models/multidimensional-allocation/MultiAllocationSheetData';
import { STATUS_INDICATOR_CONFIG } from 'src/components/multidimensional-allocation/common/Constants';
import { getPermissions } from 'src/components/AppPermissions';
import { GlobalAppContext } from 'src/components/App';
import { AppModules } from 'src/models/permissions/RolePermissions';
import CONSTANTS, { IS_MEC_RUN_ALWAYS_ENABLED } from 'src/utils/constants';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import {
    ACTIONS,
    commonBalancePoolBuilderReducer,
} from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderReducer';
import {
    effectiveDefinitionColumns,
    getInitialState,
    initialState,
    isPastPeriodView,
    isPeriodEditable,
    periodDefinitionState,
    PERIOD_DEFINITION_UNAVAILABLE,
    READ_ONLY_PAST_PERIOD_REASON,
} from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderState';
import { CommonBalancePoolBuilderContext } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderContext';
import { InputRow, SOURCE_TYPE_LABEL, sourceKeyOf, toInputRows } from './cbpInputs';
import AddCbpInputModal from './AddCbpInputModal';
import CbpCalculatedColumnsSection from './CbpCalculatedColumnsSection';
import CbpResultsSection from './CbpResultsSection';

// Execution-status polling (LLD §12.4): after an Execute, poll GetCommonBalancePoolExecutionStatus at a
// ~3–5s cadence until a terminal state; give up at the client-side timeout below and surface a
// "status unavailable — reload / contact support" message with a Reload-status button.
const EXECUTION_POLL_INTERVAL_MS = 4000;
// Give up after 35 minutes. Chosen to comfortably outlive the execution state machine's own ~28-minute
// budget, so the client keeps polling until the server run has definitively finished or failed rather than
// abandoning a still-live run. (An LLD §7.6 watchdog that would flip a stuck RUNNING run to TIMED_OUT is not
// yet implemented — nothing writes TIMED_OUT today — so this is deliberately a flat constant, not derived
// from any backend watchdog value; the missing watchdog is tracked separately.)
const EXECUTION_POLL_TIMEOUT_MS = 35 * 60 * 1000;
// Give up (and surface the error) after this many *consecutive* failed status reads rather than retrying
// silently to the timeout above — a permanent failure (bad executionId, 403/404) then shows promptly,
// while a transient blip (1–2 misses) still recovers. A successful read resets the streak.
const MAX_CONSECUTIVE_POLL_FAILURES = 3;
const TERMINAL_EXECUTION_STATES = new Set<CommonBalancePoolExecutionState>([
    'SUCCESS', 'PARTIAL', 'FAILED', 'TIMED_OUT',
]);
const isTerminalExecutionState = (s?: CommonBalancePoolExecutionState): boolean => !!s && TERMINAL_EXECUTION_STATES.has(s);
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Cloudscape StatusIndicator type per execution state — icon + text, never colour-only (LLD §12.5). */
const EXECUTION_STATE_INDICATOR: Record<CommonBalancePoolExecutionState, StatusIndicatorProps.Type> = {
    PENDING: 'pending',
    RUNNING: 'in-progress',
    SUCCESS: 'success',
    PARTIAL: 'warning',
    FAILED: 'error',
    TIMED_OUT: 'error',
};
/** "TIMED_OUT" -> "Timed out"; the human label beside the status icon. */
const executionStateLabel = (s: CommonBalancePoolExecutionState): string =>
    s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

/**
 * Accounting periods for Execute, newest first. Anchored to the newest *closed* period so Execute never
 * defaults to books that haven't closed: prod/beta close the previous period during the current calendar
 * month, so the window ends last month there, whereas dev/alpha/preprod (IS_MEC_RUN_ALWAYS_ENABLED) may
 * run the current month. Mirrors the same guard in the calculation/worksheet status bars.
 *
 * The option LABEL is the MDA "MMM-YYYY" display convention (e.g. "APR-2026"); the option VALUE is the
 * canonical `yyyyMM` period id (e.g. "202604") the CBP execute/validate/preview endpoints expect —
 * display and wire formats are kept decoupled per the package's PERIOD_DISPLAY_FORMAT /
 * PERIOD_VALUE_FORMAT split, so we never send the human-readable label to the backend as the period.
 */
function generateAccountingPeriods(openPeriod?: string, count = 12): SelectProps.Option[] {
    // Anchor the list at the backend open period when known (newest option = openPeriod), so the selector
    // defaults to the editable period. Fall back to the wall-clock newest month until the open period
    // resolves. Only that period is editable; the earlier options are read-only historical views.
    const anchor = openPeriod
        ? moment(openPeriod, CONSTANTS.PERIOD_VALUE_FORMAT)
        : (IS_MEC_RUN_ALWAYS_ENABLED ? moment() : moment().subtract(1, 'months'));
    const start = anchor.date(1);
    return Array.from({ length: count }, (_, i) => {
        const month = start.clone().subtract(i, 'months');
        return {
            label: month.format(CONSTANTS.PERIOD_DISPLAY_FORMAT).toUpperCase(),
            value: month.format(CONSTANTS.PERIOD_VALUE_FORMAT),
        };
    });
}

/**
 * A rejected update comes back with `validationResult.passed === false`; turn its BLOCKING findings
 * into a user-facing message so a refused save is surfaced as an error rather than shown as success.
 */
function blockingFindingsMessage(validationResult?: CommonBalancePoolValidationResult): string | undefined {
    if (!validationResult || validationResult.passed !== false) return undefined;
    const detail = (validationResult.findings ?? [])
        .filter((f) => f.severity === 'BLOCKING')
        .map((f) => f.message || f.code)
        .filter(Boolean)
        .join('; ');
    return detail || 'The pool was rejected by validation.';
}

function getStatusIndicator(status: string) {
    // Fall back gracefully if the backend returns a status not in the config, rather than throwing
    // on the array destructure and crashing the render.
    const [label, statusType, color] = STATUS_INDICATOR_CONFIG[status as WorksheetStatus]
        ?? ([status, 'info', undefined] as unknown as (typeof STATUS_INDICATOR_CONFIG)[WorksheetStatus]);
    return (
        <StatusIndicator type={statusType} colorOverride={color}>
            {label}
        </StatusIndicator>
    );
}

/** Memoised provider (cf. CalculationBuilderView) — value identity keyed on state/dispatch, not services. */
export const CommonBalancePoolBuilderProvider = (props: any) => {
    const { state, dispatch, services, poolId, canEdit, persistColumns, children } = props;
    const value = useMemo(
        () => ({ cbpBuilderState: state, cbpBuilderDispatch: dispatch, services, poolId, canEdit, persistColumns }),
        [state, dispatch, poolId, canEdit, persistColumns],
    );
    return <CommonBalancePoolBuilderContext.Provider value={value}>{children}</CommonBalancePoolBuilderContext.Provider>;
};

/**
 * Common Balance Pool builder — a route-mounted page (`/calculations/multidimensionalAllocation/
 * commonBalancePool/:poolId`), wrapped in ATPViewRestrictor. Follows the package's builder convention:
 * feature state lives in a reducer (`commonBalancePoolBuilderReducer`) exposed via
 * `CommonBalancePoolBuilderContext`; the inputs modal and Step-2 section consume that context rather
 * than receiving prop-threaded state.
 */
export default function CommonBalancePoolBuilder({ services }: { services: ServiceCollection }) {
    const { poolId } = useParams<{ poolId: string }>();
    const history = useHistory();
    const { canEdit } = getPermissions(AppModules.MULTIDIMENSIONAL_ALLOCATION);
    // Logged-in alias, for segregation of duties on approve/reject (mirrors TPAllocationActionBar).
    const { userProfile } = useContext(GlobalAppContext) ?? {};
    const [state, dispatch] = useReducerWithLogger(commonBalancePoolBuilderReducer, initialState, getInitialState);
    const { pool, isLoading, error, poolNeedsRefresh, saving, saveError, step2Dirty, period, actionBusy, actionError, actionNotice, segmentTables, resultsRows, resultsTotalRowCount, resultsLoading, resultsVersionResolving, resultsVersionedColumns, resultsPeriodMaterialised, executionStatus, executionTimedOut } = state;
    // Reject-comment modal (transient UI state — a prompt, not part of the reducer's domain state).
    const [rejectOpen, setRejectOpen] = useState(false);
    // A period switch requested while Step-2 has unsaved edits — held here until the user confirms discarding
    // them (switching re-syncs the section and drops the edits), so the loss is never silent.
    const [pendingPeriodSwitch, setPendingPeriodSwitch] = useState<SelectProps.Option | null>(null);
    const [rejectComment, setRejectComment] = useState('');

    // The backend's current open accounting period (yyyyMM) — the same source PromotionHistoryView anchors
    // to. Only this period is editable; a past period is a read-only historical view (Steps 1-2 render the
    // version that produced it, via effectiveDefinitionColumns). getHandOffStatus is a hook, so it's called
    // unconditionally at the top level. Mirrored into the reducer so the sections read it via the selectors.
    const [handOffQuery] = useState(() => new Date().toString());
    const [rawOpenPeriod] = services.mecService.getHandOffStatus(handOffQuery);
    // Normalise to the same yyyyMM shape the period options use, so the open-period comparison holds even if
    // the hand-off read's period shape ever drifts from yyyyMM — otherwise no option would match and every
    // period would read as "past", locking the whole builder read-only + empty. A no-op for a yyyyMM value.
    const openPeriod = useMemo(() => {
        if (!rawOpenPeriod) return undefined;
        if (moment(rawOpenPeriod, CONSTANTS.PERIOD_VALUE_FORMAT, true).isValid()) return rawOpenPeriod;
        const alt = moment(rawOpenPeriod, ['MMM-YY', 'MMM-YYYY', 'MMMM YYYY', 'YYYY-MM'], true);
        // Unparseable → undefined (NOT the raw string): fail open to the wall-clock anchor / editable, rather
        // than store a value that matches no option and would lock every period as "past".
        return alt.isValid() ? alt.format(CONSTANTS.PERIOD_VALUE_FORMAT) : undefined;
    }, [rawOpenPeriod]);
    useEffect(() => {
        dispatch(ACTIONS.SET_OPEN_PERIOD.withPayload(openPeriod || undefined));
    }, [openPeriod]);

    // Accounting periods, newest first, anchored at the backend open period once known (so the selector
    // defaults to the editable period); wall-clock newest until it resolves.
    const periodOptions = useMemo(() => generateAccountingPeriods(openPeriod || undefined), [openPeriod]);
    // Current pool for the persist merge-fallback, without making persistColumns unstable.
    const poolRef = useRef(pool);
    poolRef.current = pool;

    // Fetch the segment-CDT list once and share it through the reducer/context: the Add-input modal
    // reads the same list for its picker (no duplicate fetch), and the input source-name map derives
    // from it. The backend stores only the CDT/MDA id on `sourceRef` (no name), so on reload the Step-1
    // grid would otherwise show a raw UUID. (MDA_OUTPUT keeps any name captured at add-time.)
    const segTablesRequest = useMemo(() => ({ tableType: TableType.SEGMENT_LEVEL }), []);
    const [segTablesResp, segTablesLoading, segTablesError] = services.segmentDataTablesService.listSegmentDataTables(segTablesRequest);
    // Mirror the fetch state into the reducer, but only when it actually changes — guard on a content
    // signature (not object identity) so a new tuple reference on each render never triggers a
    // dispatch→re-render loop, and redundant dispatches are skipped.
    const segTablesSigRef = useRef<string>('');
    useEffect(() => {
        const sig = `${!!segTablesLoading}|${segTablesError ?? ''}|${(segTablesResp?.tables ?? []).map((t) => t.tableId).join(',')}`;
        if (sig === segTablesSigRef.current) return;
        segTablesSigRef.current = sig;
        dispatch(ACTIONS.SET_SEGMENT_TABLES.withPayload({ tables: segTablesResp?.tables, loading: segTablesLoading, error: segTablesError }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segTablesResp, segTablesLoading, segTablesError]);
    const cdtNameById = useMemo(
        () => new Map((segmentTables ?? []).map((t) => [t.tableId, t.tableName])),
        [segmentTables],
    );
    const resolveSourceName = useCallback(
        (col: CommonBalancePoolColumn) => (col.sourceRef?.cdtTableId ? cdtNameById.get(col.sourceRef.cdtTableId) : undefined),
        [cdtNameById],
    );

    // Fetch the pool whenever a refresh is requested (mount + explicit refresh).
    useEffect(() => {
        if (!poolNeedsRefresh) return undefined;
        let active = true;
        dispatch(ACTIONS.SET_LOADING.withPayload(true));
        services.commonBalancePoolService
            .getCommonBalancePool(poolId)
            .then((result) => { if (active) dispatch(ACTIONS.SET_POOL.withPayload(result)); })
            .catch((ex) => { if (active) dispatch(ACTIONS.SET_ERROR.withPayload(ex instanceof Error ? ex.message : String(ex))); });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [poolId, poolNeedsRefresh]);

    // Default the selector to the newest option and re-point it once the open period resolves (the list
    // re-anchors, so the newest option becomes the open period) — so the builder opens on the editable period
    // rather than a read-only past one. A user's explicit pick (userPickedPeriodRef, set in the Select's
    // onChange) is kept, unless it is no longer in the re-anchored options. Never blocks: if the open-period
    // read is empty/failed the wall-clock newest stays selected (and, being unknown, editing is not locked).
    const userPickedPeriodRef = useRef(false);
    useEffect(() => {
        const newest = periodOptions[0];
        if (!newest) return;
        const selectionStillValid = !!period && periodOptions.some((o) => o.value === period.value);
        if ((!userPickedPeriodRef.current || !selectionStillValid) && period?.value !== newest.value) {
            dispatch(ACTIONS.SET_PERIOD.withPayload(newest));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodOptions]);

    /**
     * The single UpdateCommonBalancePool choke point (with the dangling-reference guard). Returns the
     * persisted columns as the server echoed them (falling back to what was sent) on success, or null on
     * failure/refusal — the same list written to `pool.columns` here — so a caller can re-sync its local
     * working copy to the authoritative stored shape instead of to what it sent.
     */
    const persistColumns = useCallback(async (nextColumns: CommonBalancePoolColumn[]): Promise<CommonBalancePoolColumn[] | null> => {
        // A CALC column must not depend on a columnId absent from the set being persisted (e.g. removing/
        // editing an input a calculated column still references). Centralised so it covers every path.
        const knownIds = new Set(nextColumns.map((c) => c.columnId));
        const dangling = Array.from(new Set(
            nextColumns
                .filter((c) => c.kind === 'CALC')
                .flatMap((c) => c.dependsOn ?? [])
                .filter((id) => !knownIds.has(id)),
        ));
        if (dangling.length > 0) {
            dispatch(ACTIONS.SET_SAVE_ERROR.withPayload(
                `Cannot save: calculated column(s) reference removed input(s): ${dangling.join(', ')}. `
                + 'Update or remove those calculated columns first.',
            ));
            return null;
        }
        dispatch(ACTIONS.SET_SAVE_ERROR.withPayload(undefined));
        dispatch(ACTIONS.SET_SAVING.withPayload(true));
        try {
            const { pool: updated, validationResult } = await services.commonBalancePoolService.updateCommonBalancePool(poolId, { columns: nextColumns });
            // A BLOCKING validation finding means the update was refused — surface it instead of
            // reporting success on a save that never applied.
            const blocked = blockingFindingsMessage(validationResult);
            if (blocked) {
                dispatch(ACTIONS.SET_SAVE_ERROR.withPayload(blocked));
                return null;
            }
            // Trust the server's echoed columns (fall back to what we sent), matching runStatusAction —
            // both write paths take the persisted columns from the response rather than disagreeing.
            const persisted = updated?.columns ?? nextColumns;
            dispatch(ACTIONS.SET_POOL.withPayload({ ...(updated ?? poolRef.current), columns: persisted }));
            return persisted;
        } catch (ex) {
            dispatch(ACTIONS.SET_SAVE_ERROR.withPayload(ex instanceof Error ? ex.message : String(ex)));
            return null;
        } finally {
            dispatch(ACTIONS.SET_SAVING.withPayload(false));
        }
    }, [poolId, services, dispatch]);

    const handleEditInput = (row: InputRow) => dispatch(ACTIONS.OPEN_INPUT_MODAL.withPayload(row));
    const startAdd = () => dispatch(ACTIONS.OPEN_INPUT_MODAL.withPayload(undefined));

    const handleRemoveInput = (row: InputRow) => {
        const nextColumns = (poolRef.current?.columns ?? []).filter(
            (col) => !(col.kind === 'INPUT' && sourceKeyOf(col) === row.sourceKey),
        );
        persistColumns(nextColumns);
    };

    /**
     * Persist the definition, driving a status transition: Save draft / Submit for review, and the
     * reviewer's Approve (-> ACTIVE) / Reject (-> DRAFT_IN_PROGRESS). `comment` is carried on the wire
     * (required on a rejection so the author knows what to change). The backend enforces
     * segregation-of-duties (submitter != approver), so the UI need not.
     */
    const runStatusAction = async (
        busyKind: 'save' | 'submit' | 'approve' | 'reject',
        status: WorksheetStatus,
        successMessage: string,
        comment?: string,
    ): Promise<boolean> => {
        dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(undefined));
        dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(undefined));
        dispatch(ACTIONS.SET_ACTION_BUSY.withPayload(busyKind));
        try {
            // Save draft / Submit carry the draft's columns; Approve / Reject are status-only. Sending
            // columns on Approve trips the backend's "cannot be approved and edited in the same request"
            // guard (touchesContent = columns != null), and the backend preserves stored columns when the
            // request omits them.
            const includeColumns = busyKind === 'save' || busyKind === 'submit';
            const { pool: updated, validationResult } = await services.commonBalancePoolService.updateCommonBalancePool(poolId, {
                status,
                ...(includeColumns ? { columns: poolRef.current?.columns ?? [] } : {}),
                ...(comment ? { comment } : {}),
            });
            const blocked = blockingFindingsMessage(validationResult);
            if (blocked) {
                dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(blocked));
                return false;
            }
            dispatch(ACTIONS.SET_POOL.withPayload({
                ...(updated ?? poolRef.current),
                columns: updated?.columns ?? poolRef.current?.columns,
                status: updated?.status ?? status,
            }));
            dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(successMessage));
            return true;
        } catch (ex) {
            dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(ex instanceof Error ? ex.message : String(ex)));
            return false;
        } finally {
            dispatch(ACTIONS.SET_ACTION_BUSY.withPayload(''));
        }
    };

    const handleSaveDraft = () => runStatusAction('save', 'DRAFT_IN_PROGRESS', 'Draft saved.');
    const handleSubmitForReview = () => runStatusAction('submit', 'PENDING_APPROVAL', 'Submitted for review.');
    const handleApprove = () => runStatusAction('approve', 'ACTIVE', 'Pool approved.');
    // Open/close both clear any prior actionError so the reject modal (and the page-level Alert) never
    // shows a stale error from an abandoned attempt — cancelling or reopening starts clean.
    const openReject = () => {
        dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(undefined));
        setRejectOpen(true);
    };
    const closeReject = () => {
        setRejectOpen(false);
        setRejectComment('');
        dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(undefined));
    };
    // Reject requires a reason (model contract); the modal's button is disabled until one is entered.
    // Close/clear only on success — a failed reject keeps the modal open with the typed reason intact.
    const submitReject = async () => {
        const comment = rejectComment.trim();
        if (!comment) return;
        const ok = await runStatusAction('reject', 'DRAFT_IN_PROGRESS', 'Pool rejected — returned to draft.', comment);
        if (ok) closeReject();
    };

    // Monotonic run id: every Execute (and a manual "Reload status") bumps it, and a period switch/unmount
    // does too via the effects below — a poll loop abandons itself once its runId is no longer current, so a
    // superseded or orphaned poll can't dispatch stale status onto the builder.
    const executeRunRef = useRef(0);
    useEffect(() => () => { executeRunRef.current += 1; }, []); // cancel any in-flight poll on unmount
    // Bump on a period switch too — the status shown is per (pool, period), and the reducer already clears it.
    useEffect(() => { executeRunRef.current += 1; }, [period?.value]);

    // Terminal handling shared by the poll and the idempotent-re-trigger case where the 202 is already
    // terminal: SUCCESS re-syncs the Step-3 preview (rows appear, the Submit gate flips) with a success
    // notice; PARTIAL/FAILED/TIMED_OUT surfaces the run's error rather than a green success.
    const applyTerminalOutcome = useCallback((status: CommonBalancePoolExecutionStatus, periodLabel: string) => {
        if (status.state === 'SUCCESS') {
            dispatch(ACTIONS.RESULTS_REFRESH.withPayload(undefined));
            dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(`Execution completed for ${periodLabel}.`));
        } else {
            // Drop the "Execution started" notice — this run failed, so a green success Alert must not
            // linger beside the red error (reloadExecutionStatus clears it up front for the same reason).
            dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(undefined));
            dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(
                status.errorMessage || `Execution ${status.state.toLowerCase()} for ${periodLabel}.`));
        }
    }, [dispatch]);

    /**
     * Polls GetCommonBalancePoolExecutionStatus until a terminal state or the client timeout (LLD
     * §12.4), dispatching each status so the top-bar indicator updates live, then applies the terminal
     * outcome. A transient poll error is swallowed and retried next tick. Guarded by `runId` so a superseded
     * or orphaned poll (new run / period switch / unmount) abandons itself instead of dispatching stale state.
     */
    const pollExecutionStatus = useCallback(async (executionId: string, runId: number, periodLabel: string) => {
        const startedAtMs = Date.now();
        let consecutiveFailures = 0;
        let lastError: string | undefined;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            let status;
            try {
                status = await services.commonBalancePoolService.getCommonBalancePoolExecutionStatus(executionId);
            } catch (ex) {
                lastError = ex instanceof Error ? ex.message : String(ex);
                status = undefined;
            }
            if (runId !== executeRunRef.current) return; // superseded
            if (status) {
                consecutiveFailures = 0; // a good read clears the streak — a lone blip doesn't give up
                dispatch(ACTIONS.SET_EXECUTION_STATUS.withPayload(status));
                if (isTerminalExecutionState(status.state)) {
                    applyTerminalOutcome(status, periodLabel);
                    return;
                }
            } else {
                consecutiveFailures += 1;
                // A hard failure (bad executionId, 403/404, persistent 5xx) surfaces after a few misses
                // rather than sitting silently on the last non-terminal state until the client timeout.
                // Route it through the same "gave up" path as the timeout: that unblocks the Execute button
                // (executionInProgress goes false) and shows the Reload-status recovery, instead of leaving
                // the indicator stuck non-terminal with no way forward.
                if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
                    dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(undefined)); // clear the "Execution started" notice on give-up
                    dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(
                        `Couldn't read the execution status${lastError ? `: ${lastError}` : ''}.`));
                    dispatch(ACTIONS.SET_EXECUTION_TIMED_OUT.withPayload(true));
                    return;
                }
            }
            if (Date.now() - startedAtMs >= EXECUTION_POLL_TIMEOUT_MS) {
                dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(undefined)); // clear the "Execution started" notice on timeout
                dispatch(ACTIONS.SET_EXECUTION_TIMED_OUT.withPayload(true));
                return;
            }
            await sleep(EXECUTION_POLL_INTERVAL_MS);
            if (runId !== executeRunRef.current) return;
        }
    }, [services, dispatch, applyTerminalOutcome]);

    const handleExecute = async () => {
        if (!period?.value) {
            dispatch(ACTIONS.SET_ACTION_ERROR.withPayload('Select a period to execute.'));
            return;
        }
        const periodLabel = period.label ?? period.value;
        const selectedPeriod = period.value;
        dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(undefined));
        dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(undefined));
        dispatch(ACTIONS.SET_EXECUTION_STATUS.withPayload(undefined)); // clear any prior run's status/timeout
        const runId = ++executeRunRef.current;
        dispatch(ACTIONS.SET_ACTION_BUSY.withPayload('execute'));
        let executionId: string | undefined;
        let initial202State: CommonBalancePoolExecutionState | undefined;
        try {
            // The backend requires the definition version to materialise: 0 the live lane, -1 the draft
            // lane. Only an ACTIVE pool runs the live lane; everything else (draft, and the unreachable
            // pending/inactive) defaults to the draft lane, so Execute can never quietly write the live
            // lane if the canExecute gate is ever loosened. (canExecute restricts this to draft/active.)
            const version = pool?.status === 'ACTIVE' ? 0 : -1;
            const execution = await services.commonBalancePoolService.executeCommonBalancePool(poolId, selectedPeriod, version);
            executionId = execution?.executionId;
            initial202State = execution?.state ?? 'PENDING';
            // Superseded during the await — a period switch or a newer Execute bumped the run id (and the
            // reducer already cleared executionStatus). Don't re-seed the indicator for the old run onto the
            // now-current period. (The catch below and pollExecutionStatus are guarded the same way.)
            if (runId !== executeRunRef.current) {
                dispatch(ACTIONS.SET_ACTION_BUSY.withPayload(''));
                return;
            }
            // Seed the indicator from the 202 (PENDING on a fresh trigger; an idempotent re-trigger of an
            // in-flight run returns RUNNING, or a finished one a terminal state).
            dispatch(ACTIONS.SET_EXECUTION_STATUS.withPayload({
                executionId, poolId, period: selectedPeriod, state: initial202State,
            }));
            dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(`Execution started for ${periodLabel}.`));
        } catch (ex) {
            if (runId === executeRunRef.current) {
                dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(ex instanceof Error ? ex.message : String(ex)));
                dispatch(ACTIONS.SET_EXECUTION_STATUS.withPayload(undefined));
            }
            dispatch(ACTIONS.SET_ACTION_BUSY.withPayload(''));
            return;
        }
        // The 202 is accepted — the button's own busy ends here; the live indicator now tracks the run so the
        // builder isn't frozen for the (minute-scale) materialisation.
        dispatch(ACTIONS.SET_ACTION_BUSY.withPayload(''));
        if (!executionId) return;
        // A fresh trigger is PENDING → poll to completion. An idempotent re-trigger can already be terminal
        // (the run finished before this click) → apply it directly, no poll needed.
        if (isTerminalExecutionState(initial202State)) {
            applyTerminalOutcome({ executionId, poolId, period: selectedPeriod, state: initial202State }, periodLabel);
        } else {
            pollExecutionStatus(executionId, runId, periodLabel);
        }
    };

    /** "Reload status" (LLD §12.4): re-poll the last run after the poll gave up (timeout or repeated
     *  failures). Clear the prior give-up/timeout error + notice up front — same as handleExecute — so a
     *  re-poll that now reads SUCCESS doesn't leave the stale error Alert beside the new success one. */
    const reloadExecutionStatus = () => {
        const executionId = executionStatus?.executionId;
        if (!executionId) return;
        dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(undefined));
        dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(undefined));
        dispatch(ACTIONS.SET_EXECUTION_TIMED_OUT.withPayload(false));
        const runId = ++executeRunRef.current;
        pollExecutionStatus(executionId, runId, period?.label ?? period?.value ?? executionStatus?.period ?? '');
    };

    const backLink = (
        <Link onFollow={() => history.push(CONSTANTS.PAGE_NAV.MULTIDIMENSIONAL_ALLOCATION.URL)}>
            &#8592; Common Balance Pools
        </Link>
    );

    if (isLoading) {
        return (
            <div className="multidimensionalAllocationBuilder">
                <SpaceBetween direction="vertical" size="l">
                    {backLink}
                    <Box textAlign="center" padding="l">
                        <Spinner size="large" />
                        <Box variant="p">Loading common balance pool…</Box>
                    </Box>
                </SpaceBetween>
            </div>
        );
    }
    if (error) {
        return (
            <div className="multidimensionalAllocationBuilder">
                <SpaceBetween direction="vertical" size="l">
                    {backLink}
                    <Alert type="error" header="Error loading common balance pool">{error}</Alert>
                </SpaceBetween>
            </div>
        );
    }
    if (!pool) {
        return (
            <div className="multidimensionalAllocationBuilder">
                <SpaceBetween direction="vertical" size="l">
                    {backLink}
                    <Alert type="warning" header="Pool not found">No common balance pool found with id: {poolId}</Alert>
                </SpaceBetween>
            </div>
        );
    }

    // Only the backend open period is editable; a past period is a read-only historical view whose Steps 1-2
    // render the definition version that produced it. `effectiveColumns` is that period-appropriate set (the
    // working definition for the open period, else the resolved archived version); Step 1's inputs derive
    // from it so a past period shows the inputs it actually ran with.
    const periodEditable = isPeriodEditable(state);
    const effectiveColumns = effectiveDefinitionColumns(state);
    // A known open period that is not the one selected → the user is viewing a past (immutable) period.
    const viewingPastPeriod = isPastPeriodView(state);
    const defState = periodDefinitionState(state);
    const readOnlyPeriodReason = !periodEditable ? READ_ONLY_PAST_PERIOD_REASON : undefined;
    const inputRows = toInputRows(effectiveColumns, resolveSourceName);
    // Step-1 empty state mirrors Step 3's resolving / unavailable / genuinely-empty split so a past period
    // whose version could not be resolved doesn't read "No inputs configured yet." (it had inputs).
    const inputsEmptyMessage = defState === 'resolving'
        ? 'Loading the selected period’s definition…'
        : defState === 'unavailable' ? PERIOD_DEFINITION_UNAVAILABLE : 'No inputs configured yet.';
    // Single busy flag so an in-flight input persist (`saving`) and an in-flight lifecycle action
    // (`actionBusy`) each disable the other's buttons — avoids an overlapping-write / lost update.
    const busy = saving || actionBusy !== '';
    // Save draft / Submit for review are draft-only — a non-draft status disables both. Derived once
    // so the two guards (and their disabledReason strings) can't drift apart.
    const notDraft = !!pool.status && pool.status !== 'DRAFT_IN_PROGRESS';
    // Inputs and calculated columns are editable while DRAFT_IN_PROGRESS or ACTIVE. Editing an ACTIVE pool
    // is allowed and forks a draft server-side — the UpdateCommonBalancePool response comes back as
    // DRAFT_IN_PROGRESS and SET_POOL flips the UI. Only PENDING_APPROVAL (immutable: approve/reject) and
    // INACTIVE (retired) block edits, mirroring the backend's assertContentEditable.
    const contentEditable = pool.status !== 'PENDING_APPROVAL' && pool.status !== 'INACTIVE';
    // Execute runs the pool for the selected period. It is available while the pool is a DRAFT (execute
    // repeatedly to validate before review) and while ACTIVE (the monthly run) — but NOT once submitted
    // (PENDING_APPROVAL is immutable) or retired (INACTIVE). Mirrors the backend's requireMaterialisablePool.
    const canExecute = pool.status === 'DRAFT_IN_PROGRESS' || pool.status === 'ACTIVE';
    // A run is in flight while its polled status is non-terminal and the client hasn't timed out — the
    // Execute button shows this (loading) and is re-disabled so a second trigger can't stack on it.
    const executionInProgress = !isTerminalExecutionState(executionStatus?.state) && !!executionStatus && !executionTimedOut;
    // Submit for review is gated on a successful run: there must be materialized results for the selected
    // period to review. The Step-3 preview (GetCommonBalancePoolRows for the selected period) is the
    // reload-surviving signal — a run that produced rows. The backend enforces the authoritative gate (a
    // SUCCESS execution for the open period, newer than the last content change) and refuses otherwise;
    // this is the up-front UI mirror so Submit isn't offered before the pool has been run.
    const hasRunResults = resultsRows.length > 0 || (resultsTotalRowCount ?? 0) > 0;
    // Definition-version display (MDA parity). `currentVersion` is the live version number (1..N once
    // approved) or -1 for a pool with no approved version yet. Shown only when the backend supplies it
    // (absent before the versioning change ships), so the indicator never appears half-populated.
    // When viewing a PAST period, show THAT period's own resolved version (resultsPeriodVersion) instead of
    // the pool's current version — otherwise the label contradicts the period's Step-1/2 formulas on screen.
    const currentVersion = pool.currentVersion;
    const versionLabel = viewingPastPeriod
        ? (resultsVersionResolving ? '…' : (typeof state.resultsPeriodVersion === 'number' ? `v${state.resultsPeriodVersion}` : undefined))
        : (typeof currentVersion === 'number'
            ? (currentVersion >= 1 ? `v${currentVersion}` : 'Draft (unversioned)')
            : undefined);
    // Segregation of duties: the submitter must not approve/reject their own pool. The backend enforces
    // this on the wire; the UI mirrors it (like TPAllocationActionBar) so the block is visible up front.
    const isSubmitter = !!pool.submitUser && !!userProfile?.user && userProfile.user === pool.submitUser;
    const selfReviewDisabledReason = isSubmitter
        ? 'You submitted this pool for review; a different reviewer must approve or reject it.'
        : undefined;
    const inputColumnDefinitions = [
        {
            accessor: 'num',
            Header: () => <div className="cell-text-non-sortable">#</div>,
            Cell: ({ value }: any) => <div className="cell-text">{value}</div>,
        },
        {
            accessor: 'sourceType',
            Header: () => <div className="cell-text-non-sortable">Input type</div>,
            Cell: ({ row }: any) => (
                <div className="cell-text">
                    {row.original.sourceType ? SOURCE_TYPE_LABEL[row.original.sourceType] ?? row.original.sourceType : '-'}
                </div>
            ),
        },
        {
            accessor: 'sourceObject',
            Header: () => <div className="cell-text-non-sortable">Source object</div>,
            Cell: ({ value }: any) => <div className="cell-text">{value}</div>,
        },
        {
            accessor: 'columns',
            Header: () => <div className="cell-text-non-sortable">Columns</div>,
            Cell: ({ row }: any) => (
                <SpaceBetween direction="horizontal" size="xs">
                    {(row.original.columns as string[]).map((c) => (
                        <Badge key={c}>{c}</Badge>
                    ))}
                </SpaceBetween>
            ),
        },
        {
            accessor: 'grain',
            Header: () => <div className="cell-text-non-sortable">Grain</div>,
            Cell: () => <div className="cell-text">{pool.grain}</div>,
        },
        {
            accessor: 'actions',
            Header: () => <div className="cell-text-non-sortable">Action</div>,
            Cell: (e: any) => (
                <div className="rowActions">
                    <ButtonDropdown
                        className="rowContextMenu gridContextMenu"
                        ariaLabel={`Actions for ${(e.row.original as InputRow).sourceObject}`}
                        // Edit/Remove mutate the input set. Allowed while draft or active — editing an active
                        // pool forks a draft server-side — and blocked only when the pool is pending approval
                        // or retired (contentEditable), matching the backend's assertContentEditable. Also
                        // blocked while viewing a past period (!periodEditable): those are immutable history.
                        disabled={!canEdit || busy || step2Dirty || !contentEditable || !periodEditable}
                        expandToViewport
                        items={[
                            { id: 'edit', text: 'Edit' },
                            { id: 'remove', text: 'Remove' },
                        ]}
                        onItemClick={({ detail }) => {
                            const item = e.row.original as InputRow;
                            if (detail.id === 'edit') handleEditInput(item);
                            else if (detail.id === 'remove') handleRemoveInput(item);
                        }}
                    />
                </div>
            ),
        },
    ];

    return (
        <CommonBalancePoolBuilderProvider
            state={state}
            dispatch={dispatch}
            services={services}
            poolId={poolId}
            canEdit={canEdit}
            persistColumns={persistColumns}
        >
            <div className="multidimensionalAllocationBuilder">
                <SpaceBetween direction="vertical" size="l">
                    {backLink}
                    <Header variant="h1">{pool.poolName ?? pool.poolId}</Header>

                    {/* Status + Period on the left, lifecycle actions on the right (matches the worksheet builder). */}
                    <Box>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <SpaceBetween direction="horizontal" size="s" alignItems="center">
                                {/* A past (immutable) period shows ITS OWN status — a published period is Active
                                    regardless of the pool being edited into a draft for the open period. The open
                                    period (or an unknown open period) shows the pool lifecycle status. */}
                                {viewingPastPeriod ? (
                                    <Box variant="awsui-key-label" padding={{ top: 'xxs' }}>
                                        Period status:{' '}
                                        {/* From GetCommonBalancePoolPeriod (resultsPeriodMaterialised), NOT the row
                                            count — so a transient rows-read failure never reports a published
                                            period as "Not materialised". */}
                                        <StatusIndicator type={resultsVersionResolving ? 'pending' : resultsPeriodMaterialised ? 'success' : 'info'}>
                                            {resultsVersionResolving ? 'Checking…' : resultsPeriodMaterialised ? 'Active' : 'Not materialised'}
                                        </StatusIndicator>
                                    </Box>
                                ) : (
                                    <Box variant="awsui-key-label" padding={{ top: 'xxs' }}>
                                        Pool Status: {pool.status ? getStatusIndicator(pool.status) : '-'}
                                    </Box>
                                )}
                                {versionLabel && (
                                    <Box variant="awsui-key-label" padding={{ top: 'xxs' }}>Version: {versionLabel}</Box>
                                )}
                                {executionStatus?.state && (
                                    <Box variant="awsui-key-label" padding={{ top: 'xxs' }}>
                                        Execution:{' '}
                                        <StatusIndicator type={EXECUTION_STATE_INDICATOR[executionStatus.state] ?? 'in-progress'}>
                                            {executionStateLabel(executionStatus.state)}
                                        </StatusIndicator>
                                    </Box>
                                )}
                                <Box variant="awsui-key-label" padding={{ top: 'xxs' }}>Period</Box>
                                <Select
                                    selectedOption={period ?? null}
                                    onChange={({ detail }) => {
                                        userPickedPeriodRef.current = true;
                                        // Prompt before discarding unsaved Step-2 edits; otherwise switch straight away.
                                        if (step2Dirty) setPendingPeriodSwitch(detail.selectedOption);
                                        else dispatch(ACTIONS.SET_PERIOD.withPayload(detail.selectedOption));
                                    }}
                                    options={periodOptions}
                                    placeholder="Select period"
                                    disabled={periodOptions.length === 0}
                                    filteringType="auto"
                                />
                            </SpaceBetween>

                            <SpaceBetween direction="horizontal" size="s">
                                {canEdit && pool.status === 'PENDING_APPROVAL' && (
                                    <>
                                        <Button
                                            onClick={openReject}
                                            loading={actionBusy === 'reject'}
                                            disabled={busy || isSubmitter}
                                            disabledReason={selfReviewDisabledReason}
                                        >
                                            Reject
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleApprove}
                                            loading={actionBusy === 'approve'}
                                            disabled={busy || isSubmitter}
                                            disabledReason={selfReviewDisabledReason}
                                        >
                                            Approve
                                        </Button>
                                    </>
                                )}
                                <Button
                                    onClick={handleSaveDraft}
                                    loading={actionBusy === 'save'}
                                    disabled={!canEdit || busy || step2Dirty || notDraft || !periodEditable}
                                    disabledReason={
                                        !canEdit ? 'You do not have edit permissions.'
                                            : !periodEditable ? readOnlyPeriodReason
                                                : step2Dirty ? 'Save your calculated-column changes (Step 2) first.'
                                                    : notDraft ? 'Save draft is only available while the pool is a draft.'
                                                        : undefined
                                    }
                                >
                                    Save draft
                                </Button>
                                <Button
                                    onClick={handleSubmitForReview}
                                    loading={actionBusy === 'submit'}
                                    disabled={!canEdit || busy || inputRows.length === 0 || step2Dirty || notDraft || !hasRunResults || !periodEditable}
                                    disabledReason={
                                        !canEdit ? 'You do not have edit permissions.'
                                            : !periodEditable ? readOnlyPeriodReason
                                                : step2Dirty ? 'Save your calculated-column changes (Step 2) first.'
                                                    : notDraft ? 'Submit for review is only available while the pool is a draft.'
                                                        : inputRows.length === 0 ? 'Add at least one input before submitting.'
                                                            : !hasRunResults ? 'Execute the pool for the selected period, then submit the results for review.'
                                                                : undefined
                                    }
                                >
                                    Submit for review
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleExecute}
                                    loading={actionBusy === 'execute' || executionInProgress}
                                    disabled={!canEdit || busy || executionInProgress || inputRows.length === 0 || !period || step2Dirty || !canExecute || !periodEditable}
                                    disabledReason={
                                        !canEdit ? 'You do not have edit permissions.'
                                            : !periodEditable ? readOnlyPeriodReason
                                                : step2Dirty ? 'Save your calculated-column changes (Step 2) first.'
                                                    : !canExecute ? 'Execute is available while the pool is a draft or active.'
                                                        : executionInProgress ? 'A run is already in progress for this pool.'
                                                            : inputRows.length === 0 ? 'Add at least one input before executing.'
                                                                : !period ? 'Select a period to execute.'
                                                                    : undefined
                                    }
                                >
                                    Execute
                                </Button>
                            </SpaceBetween>
                        </div>
                    </Box>

                    {actionError && (
                        <Alert type="error" dismissible header="Action failed" onDismiss={() => dispatch(ACTIONS.SET_ACTION_ERROR.withPayload(undefined))}>
                            {actionError}
                        </Alert>
                    )}
                    {actionNotice && (
                        <Alert type="success" dismissible onDismiss={() => dispatch(ACTIONS.SET_ACTION_NOTICE.withPayload(undefined))}>
                            {actionNotice}
                        </Alert>
                    )}
                    {/* The poll gave up without a terminal state — either the client timeout (LLD §12.4) or
                        repeated status-read failures. The copy names both causes rather than asserting a
                        duration, since the failure give-up fires within ~8s. Offer a manual re-poll, and
                        (via executionTimedOut) unblock the Execute button. */}
                    {executionTimedOut && (
                        <Alert
                            type="warning"
                            header="Execution status unavailable"
                            action={<Button iconName="refresh" onClick={reloadExecutionStatus}>Reload status</Button>}
                        >
                            The latest execution status couldn't be confirmed — the status read may be failing, or the
                            run may still be in progress after an extended time. Reload the status, and contact Technical
                            support if it does not resolve.
                        </Alert>
                    )}
                    {/* A rejection returns the pool to draft with the reviewer's reason; surface it so the
                        author sees what to change (otherwise the mandatory reject reason is write-only). */}
                    {pool.status === 'DRAFT_IN_PROGRESS' && pool.comment && (
                        <Alert type="info" header="Returned for changes">{pool.comment}</Alert>
                    )}

                    {/* Step 1: Configure inputs */}
                    <div className="stepsWizard">
                        <Container
                            data-class="polarisExpandableSection"
                            header={<Header variant="h3">Step 1: Configure inputs</Header>}
                            footer={
                                <div className="actionButtonsContainer">
                                    <Box float="left">
                                        <Button
                                            variant="normal"
                                            onClick={startAdd}
                                            disabled={!canEdit || busy || step2Dirty || !contentEditable || !periodEditable}
                                            disabledReason={
                                                !canEdit ? 'You do not have edit permissions.'
                                                    : !periodEditable ? readOnlyPeriodReason
                                                        : step2Dirty ? 'Save your calculated-column changes (Step 2) first.'
                                                            : !contentEditable ? 'Inputs can only be changed while the pool is a draft or active.'
                                                                : undefined
                                            }
                                        >
                                            Add input
                                        </Button>
                                    </Box>
                                </div>
                            }
                        >
                            <SpaceBetween direction="vertical" size="m">
                                <Box color="text-body-secondary">
                                    Bring inputs to the {pool.grain} grain. Each input contributes one or more columns;
                                    segment CDTs and allocation outputs must match the pool grain to be selectable.
                                </Box>
                                {saveError && (
                                    <Alert type="error" dismissible header="Update failed" onDismiss={() => dispatch(ACTIONS.SET_SAVE_ERROR.withPayload(undefined))}>
                                        {saveError}
                                    </Alert>
                                )}
                                <TPEReactTable {...{
                                    data: inputRows,
                                    columnDefinitions: inputColumnDefinitions,
                                    className: 'cbpInputsGrid',
                                    sortable: false,
                                    key: 'cbpInputsGrid',
                                    emptyMessage: inputsEmptyMessage,
                                }} />
                            </SpaceBetween>
                        </Container>
                    </div>

                    {/* Step 2: Configure calculated columns — consumes the builder context. */}
                    <div className="stepsWizard">
                        <Container
                            data-class="polarisExpandableSection"
                            header={<Header variant="h3">Step 2: Configure calculated columns</Header>}
                        >
                            <CbpCalculatedColumnsSection />
                        </Container>
                    </div>

                    {/* Step 3: Validate & preview — a bounded materialized preview for the selected period.
                        The Validate / Download actions and the sum-vs-GL logic are wired separately. */}
                    <div className="stepsWizard">
                        <Container
                            data-class="polarisExpandableSection"
                            header={<Header variant="h3">Step 3: Validate &amp; preview</Header>}
                        >
                            <CbpResultsSection />
                        </Container>
                    </div>
                </SpaceBetween>

                <AddCbpInputModal />

                <Modal
                    visible={rejectOpen}
                    onDismiss={closeReject}
                    header="Reject common balance pool"
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={closeReject} disabled={actionBusy === 'reject'}>Cancel</Button>
                                <Button variant="primary" onClick={submitReject} loading={actionBusy === 'reject'} disabled={!rejectComment.trim()}>
                                    Reject
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <SpaceBetween direction="vertical" size="m">
                        {/* A failed reject keeps the modal open; surface the error in-modal (the page-level
                            actionError Alert sits behind the modal scrim, so a backend failure would
                            otherwise look like a silent no-op and invite a blind retry). */}
                        {actionError && <Alert type="error">{actionError}</Alert>}
                        <FormField label="Reason for rejection" description="Sent back to the author so they know what to change.">
                            <Textarea
                                value={rejectComment}
                                onChange={({ detail }) => setRejectComment(detail.value)}
                                placeholder="Explain why this pool is being rejected"
                                rows={3}
                            />
                        </FormField>
                    </SpaceBetween>
                </Modal>

                {/* Switching periods re-syncs Step 2 and drops any unsaved calculated-column edits — prompt
                    first so the loss is deliberate, not silent. */}
                <Modal
                    visible={!!pendingPeriodSwitch}
                    onDismiss={() => setPendingPeriodSwitch(null)}
                    header="Discard unsaved changes?"
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setPendingPeriodSwitch(null)}>Cancel</Button>
                                <Button
                                    variant="primary"
                                    onClick={() => { dispatch(ACTIONS.SET_PERIOD.withPayload(pendingPeriodSwitch)); setPendingPeriodSwitch(null); }}
                                >
                                    Discard and switch period
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    You have unsaved calculated-column changes (Step 2). Switching to another period will discard
                    them. Save or cancel those changes first if you want to keep them.
                </Modal>
            </div>
        </CommonBalancePoolBuilderProvider>
    );
}
